import { useQuery } from "@tanstack/react-query";
import { ItProjectsClient, type LinkedItProjectDto } from "@lib/ShiOneClient";
import { useApi } from "../../../utils";
import type { LinkedItProject } from "shared-ui/src/features/projects-feature/canonical";

function mapLinkedItProject(
  project: LinkedItProjectDto,
): LinkedItProject | null {
  if (!project.itProjectId || !project.title || !project.crmAccountId) {
    return null;
  }

  return {
    itProjectId: project.itProjectId,
    title: project.title,
    crmAccountId: project.crmAccountId,
  };
}

const useGetLinkedItProjects = (serviceEngagementId: number) => {
  const api = useApi(ItProjectsClient);
  const isValidServiceEngagementId =
    Number.isInteger(serviceEngagementId) && serviceEngagementId > 0;

  return useQuery<LinkedItProject[], Error>({
    queryKey: ["linked-it-projects", serviceEngagementId],
    queryFn: async () => {
      const projects = await api.getByServiceEngagement(serviceEngagementId);
      return (projects ?? [])
        .map(mapLinkedItProject)
        .filter((project): project is LinkedItProject => Boolean(project));
    },
    enabled: isValidServiceEngagementId,
  });
};

export function useGetLinkedItProjectsByRoadmap(roadmapId: string) {
  const api = useApi(ItProjectsClient);
  const isValidRoadmapId = Boolean(roadmapId);

  return useQuery<LinkedItProject[], Error>({
    queryKey: ["linked-it-projects-by-roadmap", roadmapId],
    queryFn: async () => {
      const projects = await api.getByRoadmap(roadmapId);
      return (projects ?? [])
        .map(mapLinkedItProject)
        .filter((project): project is LinkedItProject => Boolean(project));
    },
    enabled: isValidRoadmapId,
  });
}

export default useGetLinkedItProjects;
